/**
 * Session 43 — crashReporter
 *
 * Ships uncaught errors from EVERY device (browser, PWA, Android APK,
 * Windows EXE) to `POST /api/client-errors` so a "the system keeps
 * crashing" report can be investigated from the Owner dashboard
 * (Developer tools → Crash reports) or the platform admin — instead of
 * living only in that one device's localStorage.
 *
 *  - Best-effort, never throws, never blocks the UI.
 *  - De-duplicates identical messages within 30 s; max 20 / device / 10 min
 *    (server enforces the same cap).
 *  - Includes a memory snapshot (Chrome / Android WebView expose
 *    performance.memory) — the single most useful clue for "it just dies
 *    after a few hours" reports — plus the last 30 breadcrumbs (route
 *    changes + tagged actions).
 *  - Works offline: reports are queued in localStorage and flushed when
 *    the device is back online.
 */
import { DEVICE_ROLES, ROLE_SESSION_KEYS, findRoleSession } from "@/lib/authStorage";
const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") + "/api";
const QUEUE_KEY = "nfcs.crash_queue";
const _recent = new Map(); // message -> ts
const _crumbs = [];

function _deviceId() {
  try { return window.localStorage.getItem("nfcs.device_id") || null; } catch (_) { return null; }
}
function _businessCode() {
  try { return window.localStorage.getItem("nfcs.business_code") || null; } catch (_) { return null; }
}
function _session() {
  try {
    const hit = findRoleSession((s) => !!(s && s.token), { roles: DEVICE_ROLES, scope: "device" });
    if (!hit) return null;
    const { role, session: s } = hit;
    return { token: s.token, role: s.role || role, key: ROLE_SESSION_KEYS[role] };
  } catch (_) { return null; }
}
function _platform() {
  try {
    const cap = window.Capacitor;
    if (cap && typeof cap.isNativePlatform === "function" && cap.isNativePlatform()) return typeof cap.getPlatform === "function" ? cap.getPlatform() : "native";
    if (window.__TAURI__ || window.__TAURI_INTERNALS__) return "windows";
    if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) return "pwa";
  } catch (_) { /* noop */ }
  return "web";
}
function _memory() {
  try {
    const m = performance && performance.memory;
    if (!m) return undefined;
    const mb = (n) => Math.round(n / 1048576);
    return { used_mb: mb(m.usedJSHeapSize), total_mb: mb(m.totalJSHeapSize), limit_mb: mb(m.jsHeapSizeLimit) };
  } catch (_) { return undefined; }
}
function _version() {
  return process.env.REACT_APP_VERSION || process.env.REACT_APP_VERSION_CODE || undefined;
}

/** Record a breadcrumb (route change, big action). Keep the last 30. */
export function crumb(text) {
  try {
    _crumbs.push(`${new Date().toISOString().slice(11, 19)} ${String(text).slice(0, 120)}`);
    if (_crumbs.length > 30) _crumbs.shift();
  } catch (_) { /* noop */ }
}

async function _post(report, token) {
  const headers = { "Content-Type": "application/json" };
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`${API}/client-errors`, { method: "POST", headers, body: JSON.stringify(report), keepalive: true });
  if (!res.ok && res.status !== 202) throw new Error(`HTTP ${res.status}`);
}

function _enqueue(report) {
  try {
    const q = JSON.parse(window.localStorage.getItem(QUEUE_KEY) || "[]");
    q.push(report);
    window.localStorage.setItem(QUEUE_KEY, JSON.stringify(q.slice(-20)));
  } catch (_) { /* noop */ }
}

/** Flush queued reports (called on load and when back online). */
export async function flushCrashQueue() {
  let q = [];
  try { q = JSON.parse(window.localStorage.getItem(QUEUE_KEY) || "[]"); } catch (_) { q = []; }
  if (!q.length) return;
  try { window.localStorage.removeItem(QUEUE_KEY); } catch (_) { /* noop */ }
  const sess = _session();
  for (const r of q) {
    try { await _post(r, sess && sess.token); } catch (_) { _enqueue(r); }
  }
}

/**
 * Report an error. `kind`: render | error | promise | manual | resource.
 * `extra` may carry component_stack / route.
 */
export function reportClientError(kind, err, extra = {}) {
  try {
    const message = String((err && (err.message || err.reason || err.type)) || err || "Unknown error").slice(0, 1000);
    const now = Date.now();
    const last = _recent.get(message);
    if (last && now - last < 30000) return; // identical error within 30 s → skip
    _recent.set(message, now);
    if (_recent.size > 50) _recent.delete(_recent.keys().next().value);
    const sess = _session();
    const report = {
      kind,
      message,
      stack: err && err.stack ? String(err.stack).slice(0, 6000) : undefined,
      component_stack: extra.component_stack ? String(extra.component_stack).slice(0, 6000) : undefined,
      route: extra.route || (typeof window !== "undefined" ? window.location.pathname + window.location.search : undefined),
      role: (sess && sess.role) || undefined,
      device_id: _deviceId() || undefined,
      app_version: _version(),
      platform: _platform(),
      user_agent: (typeof navigator !== "undefined" && navigator.userAgent) || undefined,
      online: typeof navigator !== "undefined" ? navigator.onLine !== false : undefined,
      memory: _memory(),
      breadcrumbs: _crumbs.slice(-30),
      business_code: _businessCode() || undefined,
      occurred_at: new Date().toISOString(),
    };
    // Session 58 — structured payload (settings drift diffs etc.).
    if (extra.details && typeof extra.details === "object") report.details = extra.details;
    if (typeof navigator !== "undefined" && navigator.onLine === false) { _enqueue(report); return; }
    _post(report, sess && sess.token).catch(() => _enqueue(report));
  } catch (_) { /* never throw from the reporter */ }
}

/** Install once: window error / unhandledrejection / online flush / route crumbs. */
export function installCrashReporter() {
  if (typeof window === "undefined" || window.__nfcsCrashReporter) return;
  window.__nfcsCrashReporter = true;
  window.addEventListener("error", (e) => {
    // Resource load failures (img/script) arrive here too with no .error.
    if (e && e.error) reportClientError("error", e.error);
    else if (e && e.target && e.target !== window && (e.target.src || e.target.href)) {
      // Session 47 — only OUR OWN assets matter. Third-party scripts
      // (platform analytics / session recorders / fonts) are routinely
      // blocked by ad-blockers and Firefox tracking protection; that is
      // not an app fault and must not page the admin.
      const url = String(e.target.src || e.target.href || "");
      let sameOrigin = false;
      try { sameOrigin = new URL(url, window.location.href).origin === window.location.origin; } catch (_) { sameOrigin = false; }
      if (sameOrigin) reportClientError("resource", { message: `Failed to load ${e.target.tagName}: ${url.slice(0, 200)}` });
    }
    else if (e && e.message && !/ResizeObserver loop/.test(e.message)) reportClientError("error", { message: e.message });
  }, true);
  window.addEventListener("unhandledrejection", (e) => {
    const r = e && e.reason;
    if (r && typeof r.message === "string" && /ResizeObserver loop/.test(r.message)) return;
    // Axios / network noise is not a crash — keep only real programming errors + 5xx.
    if (r && r.isAxiosError) {
      const st = r.response && r.response.status;
      if (!st || st < 500) return;
      reportClientError("promise", { message: `HTTP ${st} ${r.config && r.config.method ? r.config.method.toUpperCase() : ""} ${r.config && r.config.url ? r.config.url : ""}`.trim(), stack: r.stack });
      return;
    }
    reportClientError("promise", r || { message: "Unhandled promise rejection" });
  });
  window.addEventListener("online", () => { flushCrashQueue(); });
  // Route breadcrumbs (pushState / popstate).
  try {
    const push = window.history.pushState;
    window.history.pushState = function (...args) { try { crumb(`route ${args[2]}`); } catch (_) { /* noop */ } return push.apply(this, args); };
    window.addEventListener("popstate", () => crumb(`route ${window.location.pathname}`));
  } catch (_) { /* noop */ }
  crumb(`boot ${window.location.pathname} · ${_platform()}`);
  setTimeout(() => { flushCrashQueue(); }, 4000);
}

export default { installCrashReporter, reportClientError, crumb, flushCrashQueue };
